// Any scheme followed by ':', except ':digit' which is a host:port
// ("example.com:8080"), not a scheme.
const NON_HTTP_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:(?!\d)/;
const HTTP_SCHEME_PATTERN = /^https?:\/\//i;
// Hostname label: alphanumeric/underscore, hyphens not at the edges.
const VALID_HOST_LABEL_PATTERN = /^[a-z0-9_]([a-z0-9_-]*[a-z0-9_])?$/i;
const MAX_PROTOCOL_LINK_LENGTH = 2048;

// Blank is valid: clearing the field is allowed.
export const isValidProtocolLink = (value?: string): boolean =>
  normalizeProtocolLink(value ?? '') !== null;

// The button's href: blank and invalid mean nothing to open, so both
// collapse to null.
export const openableProtocolLink = (value?: string): string | null =>
  normalizeProtocolLink(value ?? '') || null;

// The save payload: canonical URL, or '' to clear (validation runs first).
export const canonicalProtocolLink = (value?: string): string =>
  normalizeProtocolLink(value ?? '') ?? '';

/**
 * Returns the canonical URL, '' for blank input, or null when invalid.
 * Private: consumers use the purpose-named views above.
 */
const normalizeProtocolLink = (raw: string): string | null => {
  const trimmed = raw.trim();

  if (trimmed === '') {
    return '';
  }

  const hasExplicitScheme = HTTP_SCHEME_PATTERN.test(trimmed);

  let candidate: string;

  if (hasExplicitScheme) {
    candidate = trimmed;
  } else if (NON_HTTP_SCHEME_PATTERN.test(trimmed)) {
    // Non-http(s) scheme (javascript:, ftp://) or missing slashes
    // ('https:osf.io'): reject, never coerce into an https URL.
    return null;
  } else {
    candidate = `https://${trimmed}`;
  }

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }

  // Reject credentials: deceptive hosts (https://covidence.org@evil.com),
  // the scheme-as-username gap (mailto:1@example.com), stored passwords.
  if (url.username !== '' || url.password !== '') {
    return null;
  }

  const { hostname } = url;

  // Bracketed IPv6: the brackets already mark it as a deliberate URL.
  if (!hostname.startsWith('[')) {
    // Reject empty labels ('www.') and junk ('-.-') that URL parsing accepts.
    if (
      !hostname
        .split('.')
        .every((label) => VALID_HOST_LABEL_PATTERN.test(label))
    ) {
      return null;
    }

    // URL parsing rewrites IPv4 shorthand ('3.14' → '3.0.0.14'). Only
    // accept a numeric host the user typed verbatim.
    if (/^[\d.]+$/.test(hostname)) {
      const typedHost = candidate
        .replace(HTTP_SCHEME_PATTERN, '')
        .split(/[/?#]/)[0]
        .split(':')[0];
      if (typedHost !== hostname) {
        return null;
      }
    }

    // Scheme-less input needs a dot: bare 'test' is a typo, but
    // 'https://intranet' is a deliberate URL.
    if (!hasExplicitScheme && !hostname.includes('.')) {
      return null;
    }
  }

  // URL#toString() punycodes IDN hosts and percent-encodes paths for the
  // backend's stricter URI.parse.
  const canonical = url.toString();

  // The limit applies to the canonical value — what the backend receives.
  if (canonical.length > MAX_PROTOCOL_LINK_LENGTH) {
    return null;
  }

  return canonical;
};
